import { useEffect, useState } from "react"

/**
 * Custom hook to manage state synchronized with localStorage.
 *
 * @param key - The key under which the value is stored in localStorage.
 * @param initialValue - The initial value to use if there is no value in localStorage.
 * @returns A tuple containing the current value, a function to update the value, and a function to remove the value.
 *
 * Usage:
 * const [value, setValue, removeValue] = useLocalStorage('myKey', 'defaultValue');
 *
 * setValue('newValue'); // Updates the value in state and localStorage
 * removeValue(); // Removes the value from state and localStorage
 */
export const useLocalStorage = <T>(key: string, initialValue: T) => {
  const [value, setValue] = useState<T>(() => {
    try {
      const jsonValue = localStorage.getItem(key)
      if (jsonValue != null) return JSON.parse(jsonValue) as T
    } catch {
      console.error("Error parsing localStorage value for key:", key)
    }
    return initialValue
  })

  useEffect(() => {
    try {
      const storedValue = JSON.stringify(value)
      localStorage.setItem(key, storedValue)
    } catch {
      console.error("Error setting localStorage value for key:", key)
    }
  }, [key, value])

  const remove = () => {
    try {
      localStorage.removeItem(key)
      setValue(initialValue)
    } catch {
      console.error("Error removing localStorage value for key:", key)
    }
  }

  return [value, setValue, remove] as const
}
